import { defineStore } from "pinia"
import Cookies from "js-cookie"
import i18n, { SUPPORTED_LOCALES, type LocaleCode } from "@/i18n"
import axios from "@/plugins/axios"

const LS_KEY = "indoorad.locale"
const DEFAULT: LocaleCode = "en"

/**
 * Reads the persisted choice without falling back to navigator.language —
 * the spec we agreed on is `en` as a hard default, plus an explicit user
 * picker. Auto-guessing from the browser wandered into "why is my UI
 * suddenly French" territory in previous iterations.
 */
const readPersisted = (): LocaleCode => {
	try {
		const stored = localStorage.getItem(LS_KEY)
		if (stored && SUPPORTED_LOCALES.some((l) => l.code === stored)) {
			return stored as LocaleCode
		}
	} catch { /* SSR / privacy mode */ }
	return DEFAULT
}

/**
 * Single point of truth for the cabinet's active locale. Wraps the
 * vue-i18n instance so consumers don't reach into i18n.global directly.
 *
 * Side effects on every change:
 *   - vue-i18n.locale
 *   - <html lang="…"> and <html dir="ltr|rtl">
 *   - localStorage (anonymous + signed-in)
 *   - PATCH /auth/preferred-locale when signed in (best-effort, doesn't
 *     block the UI on a slow network)
 */
export const useLocaleStore = defineStore("locale", {
	state: () => ({
		current: readPersisted() as LocaleCode,
	}),
	getters: {
		isRtl: (state): boolean =>
			SUPPORTED_LOCALES.find((l) => l.code === state.current)?.dir === "rtl",
		meta: (state) =>
			SUPPORTED_LOCALES.find((l) => l.code === state.current) ?? SUPPORTED_LOCALES[0],
	},
	actions: {
		applyToDom(): void {
			if (typeof document === "undefined") return
			const html = document.documentElement
			html.setAttribute("lang", this.current)
			// Keep the document LTR even for Arabic. Unicode bidi handles
			// arabic glyphs within text runs automatically; flipping the
			// whole layout would require a real RTL pass on every Naive UI
			// popover (their JS positioning math is one-way) and isn't
			// worth the regression risk for a cabinet whose chrome is
			// LTR-shaped. If we ever invest in proper RTL, this is the
			// single line to change.
			html.setAttribute("dir", "ltr")
		},
		applyToI18n(): void {
			// vue-i18n typings narrow locale to a literal union — assert because
			// the store value is dynamic by definition.
			(i18n.global.locale as unknown as { value: LocaleCode }).value = this.current
		},
		/** Idempotent — safe to call from boot and from the picker. */
		setLocale(code: LocaleCode, opts: { syncToBackend?: boolean } = {}): void {
			if (!SUPPORTED_LOCALES.some((l) => l.code === code)) return
			this.current = code
			this.applyToI18n()
			this.applyToDom()
			try { localStorage.setItem(LS_KEY, code) } catch { /* ignore */ }
			if (opts.syncToBackend !== false) void this.pushToBackend()
		},
		/**
		 * Called by authStore.syncFromServer when the user's BD-stored
		 * preference differs from the local choice. Server wins on first
		 * load after login so a user that picked Russian on phone sees
		 * Russian on web too.
		 */
		adoptFromServer(serverLocale: string | null | undefined): void {
			if (!serverLocale) return
			if (!SUPPORTED_LOCALES.some((l) => l.code === serverLocale)) return
			if (serverLocale === this.current) return
			this.setLocale(serverLocale as LocaleCode, { syncToBackend: false })
		},
		async pushToBackend(): Promise<void> {
			// Only sync when authenticated. On auth pages (login/register) there
			// is no token: the PATCH would 401, the axios interceptor would then
			// try to refresh, fail (no refresh token), clear the cookie and
			// redirect to /login — kicking the user out of registration just for
			// switching language. Skip the call entirely when not signed in; the
			// choice lives in localStorage until login (synced after sign-in).
			if (!Cookies.get("auth")) return
			try {
				await axios.patch("/api/v1/auth/preferred-locale", { locale: this.current })
			} catch { /* offline or token just expired — fine */ }
		},
	},
})
